/**
 * Story 18.8 — iPhone-first device-aware picker; see
 * _bmad-output/implementation-artifacts/18-8-device-aware-photo-picker.md.
 *
 * Tiny User-Agent sniffing helper exposing a single `isIos()` boolean.
 * Used by the upload affordance (`<UploadDropZone>`) to surface a
 * device-class hook on the rendered `<input>` element. v1 picker output
 * is identical across devices (no `capture` attribute everywhere); the
 * helper is a future-proofing seam for stories that want
 * device-conditional branches.
 *
 * Public API (named exports only — no default export):
 *   - isIos(): boolean
 *
 * Detection contract (CSD #1 — User-Agent sniffing locked for v1):
 *   - Lowercased UA contains 'iphone' OR 'ipod' → true.
 *   - Lowercased UA contains 'ipad' → false (CSD #2 — iPad excluded).
 *   - Anything else → false (Android, desktop, anything not in scope).
 *   - SSR-safe (CSD #6 — `typeof navigator === 'undefined'` short-circuits).
 *
 * Trade-offs documented in the story spec under CSDs #1-#6:
 *   - Feature-detection (`'capture' in input`) rejected per CSD #1
 *     (doesn't answer the right question + library weight).
 *   - iPad excluded per CSD #2 (user said "iPhone only"; iPad UA is
 *     unreliable on iPadOS 13+ which can identify as Macintosh).
 *   - Android falls through to default per CSD #3 (out of scope for
 *     v1).
 *   - One-shot at render scope per CSD #4 (no resize/orientation
 *     listeners; UA does not change mid-session).
 *   - SSR-safe per CSD #6 (Vite does not SSR today, but defensive).
 */

// Story 18.8 — iPhone-first device-aware picker.
export function isIos(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent.toLowerCase();
  // CSD #2 — iPad explicitly excluded from iOS branch.
  if (ua.includes('ipad')) {
    return false;
  }
  return ua.includes('iphone') || ua.includes('ipod');
}
